import React from 'react';
import { SportIcon } from '@/components/SportIcon';

interface SportCardBannerProps {
  sport: string;
  isLive?: boolean;
  isNeedOne?: boolean;
  timeUntil?: string;
}

const SPORT_THEMES: Record<string, {
  gradient: string;
  accent: string;
  subtle: string;
  courtPattern: 'cricket' | 'football' | 'basketball' | 'badminton' | 'tennis' | 'default';
}> = {
  CRICKET: {
    gradient: 'linear-gradient(135deg, #064E3B 0%, #065F46 45%, #059669 100%)',
    accent: '#34D399',
    subtle: 'rgba(52, 211, 153, 0.15)',
    courtPattern: 'cricket',
  },
  FOOTBALL: {
    gradient: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 45%, #0284C7 100%)',
    accent: '#38BDF8',
    subtle: 'rgba(56, 189, 248, 0.15)',
    courtPattern: 'football',
  },
  BASKETBALL: {
    gradient: 'linear-gradient(135deg, #7C2D12 0%, #C2410C 45%, #EA580C 100%)',
    accent: '#FB923C',
    subtle: 'rgba(251, 146, 60, 0.15)',
    courtPattern: 'basketball',
  },
  BADMINTON: {
    gradient: 'linear-gradient(135deg, #4C1D95 0%, #6D28D9 45%, #8B5CF6 100%)',
    accent: '#A78BFA',
    subtle: 'rgba(167, 139, 250, 0.15)',
    courtPattern: 'badminton',
  },
  TENNIS: {
    gradient: 'linear-gradient(135deg, #713F12 0%, #A16207 45%, #65A30D 100%)',
    accent: '#A3E635',
    subtle: 'rgba(163, 230, 53, 0.15)',
    courtPattern: 'tennis',
  },
};

const DEFAULT_THEME = {
  gradient: 'linear-gradient(135deg, #312E81 0%, #4338CA 45%, #6366F1 100%)',
  accent: '#818CF8',
  subtle: 'rgba(129, 140, 248, 0.15)',
  courtPattern: 'default' as const,
};

function CourtOverlay({ pattern }: { pattern: string }) {
  switch (pattern) {
    case 'cricket':
      return (
        <svg className="court-svg" viewBox="0 0 300 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="150" cy="65" rx="130" ry="55" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeDasharray="4 4" />
          <rect x="130" y="35" width="40" height="60" rx="3" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" fill="rgba(255,255,255,0.04)" />
          <line x1="130" y1="48" x2="170" y2="48" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
          <line x1="130" y1="82" x2="170" y2="82" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
          <circle cx="150" cy="65" r="3" fill="rgba(255,255,255,0.3)" />
        </svg>
      );
    case 'football':
      return (
        <svg className="court-svg" viewBox="0 0 300 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="15" width="260" height="100" rx="4" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <line x1="150" y1="15" x2="150" y2="115" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <circle cx="150" cy="65" r="24" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <rect x="20" y="38" width="34" height="54" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <rect x="246" y="38" width="34" height="54" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
        </svg>
      );
    case 'basketball':
      return (
        <svg className="court-svg" viewBox="0 0 300 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="15" width="260" height="100" rx="4" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <line x1="150" y1="15" x2="150" y2="115" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <circle cx="150" cy="65" r="22" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <path d="M 20 28 A 45 45 0 0 1 20 102" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" fill="none" />
          <path d="M 280 28 A 45 45 0 0 0 280 102" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" fill="none" />
          <rect x="20" y="44" width="44" height="42" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
          <rect x="236" y="44" width="44" height="42" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
        </svg>
      );
    case 'badminton':
      return (
        <svg className="court-svg" viewBox="0 0 300 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="25" y="18" width="250" height="94" stroke="rgba(255,255,255,0.16)" strokeWidth="1.5" />
          <line x1="150" y1="18" x2="150" y2="112" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <line x1="110" y1="18" x2="110" y2="112" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" />
          <line x1="190" y1="18" x2="190" y2="112" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" />
          <line x1="25" y1="65" x2="110" y2="65" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" />
          <line x1="190" y1="65" x2="275" y2="65" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" />
        </svg>
      );
    default:
      return (
        <svg className="court-svg" viewBox="0 0 300 130" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="150" cy="65" r="45" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeDasharray="3 3" />
          <circle cx="150" cy="65" r="70" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
        </svg>
      );
  }
}

export function SportCardBanner({
  sport,
  isLive,
  isNeedOne,
  timeUntil,
}: SportCardBannerProps) {
  const upper = (sport || '').toUpperCase();
  const theme = SPORT_THEMES[upper] || DEFAULT_THEME;

  return (
    <div
      className="sport-card-banner"
      style={{
        position: 'relative',
        height: '136px',
        background: theme.gradient,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, opacity: 0.9, pointerEvents: 'none' }}>
        <CourtOverlay pattern={theme.courtPattern} />
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.35))',
        }}
      >
        <div style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SportIcon sport={sport} size={42} color="#ffffff" />
        </div>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.9)',
            textShadow: '0 2px 4px rgba(0,0,0,0.4)',
          }}
        >
          {sport}
        </span>
      </div>

      <div
        className="game-card-sport-badge"
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(15, 17, 23, 0.75)',
          backdropFilter: 'blur(8px)',
          color: '#ffffff',
          fontSize: '11px',
          fontWeight: 700,
          padding: '4px 10px',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.18)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        <SportIcon sport={sport} size={13} color="#ffffff" />
        <span>{sport}</span>
      </div>

      {isLive ? (
        <div
          className="live-badge"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#EF4444',
            color: '#ffffff',
            fontSize: '10px',
            fontWeight: 800,
            letterSpacing: '0.06em',
            padding: '4px 10px',
            borderRadius: '20px',
            boxShadow: '0 2px 10px rgba(239, 68, 68, 0.5)',
          }}
        >
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#ffffff',
              boxShadow: '0 0 6px #ffffff',
            }}
          />
          LIVE
        </div>
      ) : timeUntil ? (
        <div
          className="time-badge"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 2,
            background: 'rgba(15, 17, 23, 0.75)',
            backdropFilter: 'blur(8px)',
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {timeUntil}
        </div>
      ) : null}

      <style>{`
        .court-svg {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      `}</style>
    </div>
  );
}

export function SportAvatarBadge({ sport }: { sport?: string }) {
  const upper = (sport || '').toUpperCase();
  const theme = SPORT_THEMES[upper] || DEFAULT_THEME;

  return (
    <div
      style={{
        width: '42px',
        height: '42px',
        borderRadius: '12px',
        flexShrink: 0,
        background: theme.gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.15)',
      }}
      title={sport}
    >
      <SportIcon sport={sport} size={22} color="#ffffff" />
    </div>
  );
}
